import type L from 'leaflet';

/**
 * One tooltip/popup design for every Leaflet map in CartLens (styles live in
 * src/app/globals.css under `.cl-map-tip`). All text is HTML-escaped: store
 * names come from receipts, OCR and web search, so interpolating them raw
 * into Leaflet's HTML strings would let a crafted name inject markup.
 */

export const escapeHtml = (value: unknown): string =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

export type MapTipTone = 'emerald' | 'amber' | 'indigo' | 'rose' | 'slate';

export interface MapTipContent {
  /** Small uppercase label above the title (e.g. "Stop 2", "3 trips"). */
  eyebrow?: string;
  title: string;
  /** Muted line under the title, typically an address. */
  subtitle?: string;
  /** Up to three label/value pairs shown side by side. */
  stats?: Array<{ label: string; value: string; tone?: MapTipTone }>;
  /** Small status pill at the bottom. */
  badge?: { text: string; tone?: MapTipTone };
}

export const mapTipHtml = ({ eyebrow, title, subtitle, stats, badge }: MapTipContent): string => {
  const statsHtml = stats?.length
    ? `<div class="cl-map-tip__stats">${stats
        .slice(0, 3)
        .map(
          (s) => `<div class="cl-map-tip__stat">
            <span class="cl-map-tip__stat-label">${escapeHtml(s.label)}</span>
            <span class="cl-map-tip__stat-value cl-map-tip--${s.tone ?? 'slate'}">${escapeHtml(s.value)}</span>
          </div>`,
        )
        .join('')}</div>`
    : '';
  return `<div class="cl-map-tip__body">
    ${eyebrow ? `<div class="cl-map-tip__eyebrow">${escapeHtml(eyebrow)}</div>` : ''}
    <div class="cl-map-tip__title">${escapeHtml(title)}</div>
    ${subtitle ? `<div class="cl-map-tip__subtitle">${escapeHtml(subtitle)}</div>` : ''}
    ${statsHtml}
    ${badge ? `<div class="cl-map-tip__badge cl-map-tip--${badge.tone ?? 'emerald'}"><span></span>${escapeHtml(badge.text)}</div>` : ''}
  </div>`;
};

/** Hover tooltip options shared by every map. */
export const mapTooltipOptions = (overrides: L.TooltipOptions = {}): L.TooltipOptions => ({
  direction: 'top',
  offset: [0, -14],
  opacity: 1,
  className: 'cl-map-tip',
  ...overrides,
});

/** Click popup options with the same look as the tooltips. autoPan keeps them inside the map. */
export const mapPopupOptions = (overrides: L.PopupOptions = {}): L.PopupOptions => ({
  className: 'cl-map-tip-popup',
  closeButton: false,
  maxWidth: 260,
  offset: [0, -8],
  autoPan: true,
  autoPanPadding: [24, 24],
  ...overrides,
});

const EDGE_MARGIN = 8;

/**
 * Map cards clip anything outside their rounded bounds, so a tooltip that
 * always opens upward gets cut off for markers near an edge. On every
 * tooltip open, flip it below the marker when there's no room above, and to
 * the side when it would spill past the left/right edge. Call once per map.
 */
export const enableSmartTooltipPlacement = (map: L.Map) => {
  map.on('tooltipopen', (e: L.TooltipEvent) => {
    const tooltip = e.tooltip as L.Tooltip & { options: L.TooltipOptions & { _clBaseGap?: number } };
    const latlng = tooltip.getLatLng();
    const el = tooltip.getElement();
    if (!latlng || !el) return;

    // Remember the configured gap (e.g. taller pin icons use a bigger offset).
    if (tooltip.options._clBaseGap === undefined) {
      const offset = (tooltip.options.offset as L.PointExpression) ?? [0, -14];
      const [ox, oy] = Array.isArray(offset) ? offset : [offset.x, offset.y];
      tooltip.options._clBaseGap = Math.max(Math.abs(ox), Math.abs(oy), 10);
    }
    const gap = tooltip.options._clBaseGap;

    const point = map.latLngToContainerPoint(latlng);
    const size = map.getSize();
    const width = el.offsetWidth;
    const height = el.offsetHeight;

    let direction: L.Direction = 'top';
    if (point.y - gap - height < EDGE_MARGIN) direction = 'bottom';
    if (point.x - width / 2 < EDGE_MARGIN) direction = 'right';
    else if (point.x + width / 2 > size.x - EDGE_MARGIN) direction = 'left';

    const offsets: Record<string, [number, number]> = {
      top: [0, -gap],
      bottom: [0, Math.min(gap, 14)],
      left: [-14, -gap / 2 + 7],
      right: [14, -gap / 2 + 7],
    };
    if (tooltip.options.direction === direction) return;
    tooltip.options.direction = direction;
    tooltip.options.offset = offsets[direction];
    tooltip.update();
  });
};
